import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import { useTranslation } from 'react-i18next';
import variabilityIcon from '../../../../assets/icons/vi_icon.svg';
import heteroIcon from '../../../../assets/icons/hi_icon.svg';
import consistencyIcon from '../../../../assets/icons/ci_icon.svg';
import areaIcon from '../../../../assets/icons/field_area.svg';
import infoIcon from '../../../../assets/icons/info_icon.svg';
import { SpanStyle, StyledVcLabelNormal, StyledValue, VIData, VIDataDiv, Button } from './styles/vaStyles';
import {CarouselContainer, StyledCarousel } from '../RecommendationProductsCard/RecommendationProductsCard.styles';
import { getVCLabel, getValueLabel, getTooltipLablesForVC } from './helper/vaHelper';
import {trackVAReportAccessed} from 'utils/helpers/amplitude';

const getVAReportUrl = (vaReportId: string) => {
  const vaReportBaseUrl = (process.env.REACT_APP_VA_REPORT_URL || '').replace(/\/$/, '');
  const currentUrlParams = new globalThis.URLSearchParams(globalThis.location.search);
  const allowedUtmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'lang'];
  const vaReportParams = new globalThis.URLSearchParams({ opened_via: 'osr_map' });
  const invalidTrackingValues = ['null', 'undefined'];
  const getValidTrackingValue = (value: string | null) => {
    const sanitizedValue = value?.trim();
    if (!sanitizedValue) {
      return null;
    }
    return invalidTrackingValues.includes(sanitizedValue.toLowerCase()) ? null : sanitizedValue;
  };

  allowedUtmParams.forEach((param) => {
    const validValue = getValidTrackingValue(currentUrlParams.get(param));
    if (validValue) {
      vaReportParams.set(param, validValue);
    }
  });

  if (!vaReportParams.get('lang')) {
    const fallbackLang =
      getValidTrackingValue(globalThis.sessionStorage?.getItem('langFromRecommendationPage')) ||
      getValidTrackingValue(globalThis.localStorage?.getItem('i18nextLng'));

    if (fallbackLang) {
      vaReportParams.set('lang', fallbackLang);
    }
  }

  return `${vaReportBaseUrl}/${vaReportId}?${vaReportParams.toString()}`;
};

const FieldVariabilityDataBox = (props:any) => {
  const { t } = useTranslation();
  const {fieldData, isMobile, vaReportId, userEmail} = props;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const {variability_index} = fieldData;

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    draggable: true,
    centerMode: isMobile || false,
    centerPadding: '0px',
  };

  const VIDataComponent = () => {
    return (
      <VIData isMobile={isMobile}>
        <VIDataDiv>
          <div className='cardContent'>
            <div className='title'>
              <img
                src={variabilityIcon}
                alt='vc logo'
              />
              <SpanStyle>
                {t('Variability Index')}
              </SpanStyle>
              <Tooltip
                title={getTooltipLablesForVC(t).VCIndex}
              >
                <img
                  src={infoIcon}
                  alt='info'
                  className='info-icon'
                />
              </Tooltip>
            </div>
            <div className='index_value_div'>
              {getVCLabel(variability_index.vxc, t)}
              <StyledValue>{variability_index.vxc ? Number(variability_index.vxc).toFixed(2) : 'N/A'}</StyledValue>
            </div>
          </div>
          <div className='cardContent'>
            <div className='title'>
              <img src={heteroIcon} alt='hi logo' />
              <SpanStyle>
                {t('Heterogenity Index')}
              </SpanStyle>
              <Tooltip
                title={getTooltipLablesForVC(t).Variability}
              >
                <img
                  src={infoIcon}
                  alt='info'
                  className='info-icon'
                />
              </Tooltip>
            </div>
            <div className='index_value_div'>
              <StyledVcLabelNormal className='indexValue'>
                {t(getValueLabel(variability_index.variability) ?? '')}
              </StyledVcLabelNormal>
              <StyledValue>{variability_index.variability ? Number(variability_index.variability).toFixed(2) : 'N/A'}</StyledValue>
            </div>
          </div>
          <div className='cardContent'>
            <div className='title'>
              <img
                src={consistencyIcon}
                alt='ci logo'
              />
              <SpanStyle>
                {t('Consistency Index')}
              </SpanStyle>
              <Tooltip
                title={getTooltipLablesForVC(t).Consistency}
              >
                <img
                  src={infoIcon}
                  alt='info'
                  className='info-icon'
                />
              </Tooltip>
            </div>
            <div className='index_value_div'>
              <StyledVcLabelNormal className='indexValue'>
                {t(getValueLabel(variability_index.consistency) ?? '')}
              </StyledVcLabelNormal>
              <StyledValue>{variability_index.consistency ? Number(variability_index.consistency).toFixed(2) : 'N/A'}</StyledValue>
            </div>
          </div>
          <div className='cardContent'>
            <div className='title'>
              <img src={areaIcon} alt='area logo' />
              {t('Field Area')}
            </div>
            <div className='index_value_div'>
              <StyledValue>
                {fieldData.declared_area ? Number(fieldData.declared_area).toFixed(2) : 'N/A'} ha
              </StyledValue>
            </div>
          </div>
          <div className='cardContent' style={{justifyContent: 'center'}}>
            <Button
              data-testid="va-button"
              onClick={() => {
                trackVAReportAccessed(userEmail);
                window.open(getVAReportUrl(vaReportId), '_blank');
              }}
            >
              {t('Access your FVA Report')}
            </Button>
          </div>
        </VIDataDiv>
      </VIData>
    );
  };
  return (
    isMobile ? (
      <CarouselContainer width={'auto'}>
        <StyledCarousel {...settings} isVABox={true}>
          <VIDataComponent />
        </StyledCarousel>
      </CarouselContainer>
    ) : (
      <VIDataComponent />
    )
  );
};

export default FieldVariabilityDataBox;
